import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState } from "@/components/app/ui-bits";
import {
  Plus, Trash, PencilSimple, Scales, Shield, UsersThree, BellRinging,
  ArrowRight, XCircle, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PermissionMatrixPanel from "./PermissionMatrixPanel";

const ROLE_OPTIONS = [
  { v: "finance_officer", l: "Finance Officer" },
  { v: "finance_manager", l: "Finance Manager" },
  { v: "management", l: "Management" },
  { v: "ceo", l: "CEO" },
  { v: "admin", l: "Admin" },
  { v: "super_admin", l: "Super Admin" },
];
const TXN_TYPES = ["any", "PAYMENT", "VENDOR_PAYMENT", "PETTY_CASH_REPLENISHMENT", "INVOICE", "BUDGET_OVERRUN", "TAX_PAYMENT", "FX_CONVERSION"];

// ─────────────── Rule create/edit dialog ───────────────
function RuleDialog({ open, onOpenChange, initial, onSaved }) {
  const BLANK = { band_label: "", transaction_type: "any", min_amount: 0, max_amount: 0, evidence: "", escalation_hours: 72, steps: [{ label: "", roles: [] }] };
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...initial, steps: initial.steps.map((s) => ({ ...s })) } : BLANK);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const setStep = (i, k, v) => setForm((f) => ({ ...f, steps: f.steps.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const toggleRole = (i, role) => setStep(i, "roles", form.steps[i].roles.includes(role)
    ? form.steps[i].roles.filter((r) => r !== role)
    : [...form.steps[i].roles, role]);

  const save = async () => {
    if (!form.band_label.trim()) { toast.error("Band label is required"); return; }
    if (form.steps.some((s) => !s.label.trim() || s.roles.length === 0)) { toast.error("Every step needs a label and at least one role"); return; }
    setBusy(true);
    try {
      const payload = { ...form, min_amount: parseFloat(form.min_amount) || 0, max_amount: parseFloat(form.max_amount) || 0, escalation_hours: parseInt(form.escalation_hours) || 72 };
      if (initial?.id) await api.put(`/finance/controls/matrix-rules/${initial.id}`, payload);
      else await api.post("/finance/controls/matrix-rules", payload);
      toast.success(initial?.id ? "Rule updated" : "Rule created");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rule-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit approval rule" : "New approval rule"}</DialogTitle>
          <DialogDescription>Approval steps run sequentially. Each step lists the roles that may decide it.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Band label</Label>
            <Input value={form.band_label} onChange={(e) => setForm({ ...form, band_label: e.target.value })} placeholder="$501 – $5,000" data-testid="rule-band-label" />
          </div>
          <div>
            <Label>Transaction type</Label>
            <Select value={form.transaction_type} onValueChange={(v) => setForm({ ...form, transaction_type: v })}>
              <SelectTrigger data-testid="rule-txn-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TXN_TYPES.map((t) => <SelectItem key={t} value={t}>{t === "any" ? "Any (default)" : t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Min amount ($)</Label>
            <Input type="number" step="0.01" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} data-testid="rule-min-amount" />
          </div>
          <div>
            <Label>Max amount ($)</Label>
            <Input type="number" step="0.01" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: e.target.value })} data-testid="rule-max-amount" />
          </div>
          <div>
            <Label>Escalation (hours)</Label>
            <Input type="number" value={form.escalation_hours} onChange={(e) => setForm({ ...form, escalation_hours: e.target.value })} data-testid="rule-escalation-hours" />
          </div>
          <div>
            <Label>Required evidence</Label>
            <Input value={form.evidence || ""} onChange={(e) => setForm({ ...form, evidence: e.target.value })} placeholder="Receipt + memo" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Approval steps (sequential)</Label>
          {form.steps.map((s, i) => (
            <div key={i} className="rounded-md border border-slate-200 p-3 space-y-2" data-testid={`rule-step-${i}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono-alt text-xs font-bold text-slate-500">#{i + 1}</span>
                <Input className="h-8" value={s.label} onChange={(e) => setStep(i, "label", e.target.value)} placeholder="Step label (e.g. Finance Lead)" data-testid={`rule-step-label-${i}`} />
                <Button variant="outline" size="sm" className="h-8 px-2" disabled={form.steps.length <= 1}
                  onClick={() => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }))}>
                  <Trash size={12} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ROLE_OPTIONS.map((r) => (
                  <button key={r.v} type="button" onClick={() => toggleRole(i, r.v)}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${s.roles.includes(r.v) ? "border-[#1E5CBA] bg-[#1E5CBA] text-white" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"}`}
                    data-testid={`rule-step-${i}-role-${r.v}`}>
                    {r.l}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, steps: [...f.steps, { label: "", roles: [] }] }))} data-testid="rule-add-step">
            <Plus size={12} className="mr-1" /> Add step
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-primary-blue" onClick={save} disabled={busy} data-testid="rule-save-btn">{busy ? "Saving…" : "Save rule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Approval matrix (configurable) ───────────────
function MatrixSection({ isAdmin }) {
  const [rules, setRules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/finance/controls/matrix-rules"); setRules(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const deactivate = async (id) => {
    try { await api.delete(`/finance/controls/matrix-rules/${id}`); toast.success("Rule deactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="dc-card overflow-hidden" data-testid="matrix-section">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <Scales size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Approval Matrix</h3>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">Configurable · enforced server-side</span>
        {isAdmin && (
          <Button size="sm" className="btn-primary-blue ml-auto h-7" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="rule-add-btn">
            <Plus size={12} className="mr-1" /> New rule
          </Button>
        )}
      </div>
      <div className="responsive-table-wrap">
        <table className="data-table w-full min-w-[960px]" data-testid="matrix-rules-table">
          <thead><tr><th>Band</th><th>Type</th><th className="text-right">Range</th><th>Approval steps</th><th>Evidence</th><th className="text-right">Escalate</th>{isAdmin && <th className="w-24">Actions</th>}</tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} data-testid={`matrix-rule-row-${r.id}`}>
                <td className="px-5 py-3 font-mono-alt text-sm font-semibold text-slate-900">{r.band_label}</td>
                <td className="text-xs uppercase tracking-wider text-slate-600">{r.transaction_type === "any" ? "Any" : r.transaction_type.replace(/_/g, " ")}</td>
                <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(r.min_amount)} – {formatCurrency(r.max_amount)}</td>
                <td>
                  <div className="flex flex-wrap items-center gap-1">
                    {r.steps.map((s, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <ArrowRight size={10} className="text-slate-400" />}
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700" title={s.roles.join(", ")}>{s.label}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </td>
                <td className="text-xs text-slate-600">{r.evidence || "—"}</td>
                <td className="text-right font-mono-alt text-xs tabular-nums">{r.escalation_hours}h</td>
                {isAdmin && (
                  <td>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`rule-edit-${r.id}`}><PencilSimple size={12} /></Button>
                      <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => deactivate(r.id)} data-testid={`rule-deactivate-${r.id}`}><XCircle size={12} /></Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RuleDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={load} />
    </div>
  );
}

// ─────────────── Delegations ───────────────
function DelegationsSection({ canEdit }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ delegate_email: "", reason: "", start_date: "", end_date: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/finance/controls/delegations"); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/finance/controls/delegations", form);
      toast.success("Delegation created");
      setOpen(false);
      setForm({ delegate_email: "", reason: "", start_date: "", end_date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try { await api.post(`/finance/controls/delegations/${id}/revoke`); toast.success("Delegation revoked"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="dc-card overflow-hidden" data-testid="delegations-section">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <UsersThree size={18} weight="fill" className="text-indigo-600" />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Approval Delegations</h3>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">Temporary acting authority</span>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue ml-auto h-7" onClick={() => setOpen(true)} data-testid="delegation-add-btn">
            <Plus size={12} className="mr-1" /> Delegate
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-6 text-sm text-slate-500" data-testid="delegations-empty">No delegations. Managers can temporarily delegate their approval authority while away.</div>
      ) : (
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[860px]" data-testid="delegations-table">
            <thead><tr><th>Delegator</th><th>Delegate</th><th>Window</th><th>Reason</th><th>Status</th><th className="w-24">Actions</th></tr></thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} data-testid={`delegation-row-${d.id}`}>
                  <td className="px-5 py-3 text-sm font-semibold text-slate-900">{d.delegator_email}</td>
                  <td className="text-sm">{d.delegate_email}</td>
                  <td className="font-mono-alt text-xs tabular-nums">{formatDate(d.start_date)} → {formatDate(d.end_date)}</td>
                  <td className="text-xs text-slate-600 max-w-[220px] truncate" title={d.reason}>{d.reason}</td>
                  <td>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${d.status === "revoked" ? "bg-slate-100 text-slate-500" : d.is_active_now ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {d.status === "revoked" ? "Revoked" : d.is_active_now ? "Active now" : "Scheduled"}
                    </span>
                  </td>
                  <td>
                    {d.status === "active" && (
                      <Button variant="outline" size="sm" className="h-7" onClick={() => revoke(d.id)} data-testid={`delegation-revoke-${d.id}`}>Revoke</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="delegation-dialog">
          <DialogHeader>
            <DialogTitle>New delegation</DialogTitle>
            <DialogDescription>Your approval authority passes to the delegate for the chosen window. Fully audited.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Delegate email</Label>
              <Input value={form.delegate_email} onChange={(e) => setForm({ ...form, delegate_email: e.target.value })} placeholder="user@descon.group" data-testid="delegation-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="delegation-start" />
              </div>
              <div>
                <Label>End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="delegation-end" />
              </div>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Annual leave, out of office…" data-testid="delegation-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary-blue" onClick={create} disabled={busy} data-testid="delegation-save-btn">{busy ? "Saving…" : "Create delegation"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────── Escalations ───────────────
function EscalationsSection({ canEdit }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/finance/controls/escalations"); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const notify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/controls/escalations/notify");
      toast.success(`${data.notifications_sent} notification(s) sent for ${data.overdue} overdue approval(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="dc-card overflow-hidden" data-testid="escalations-section">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <BellRinging size={18} weight="fill" className="text-amber-600" />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Overdue Approvals (Escalation)</h3>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{items.length} overdue</span>
        {canEdit && items.length > 0 && (
          <Button size="sm" variant="outline" className="ml-auto h-7" onClick={notify} disabled={busy} data-testid="escalation-notify-btn">
            <PaperPlaneTilt size={12} className="mr-1" /> Notify approvers
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-6 text-sm text-slate-500" data-testid="escalations-empty">Nothing overdue — all pending approvals are inside their escalation windows.</div>
      ) : (
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[860px]" data-testid="escalations-table">
            <thead><tr><th>Approval</th><th className="text-right">Amount</th><th>Stage</th><th className="text-right">Age</th><th className="text-right">Overdue by</th><th>Escalated</th></tr></thead>
            <tbody>
              {items.map((fa) => (
                <tr key={fa.id} data-testid={`escalation-row-${fa.id}`}>
                  <td className="px-5 py-3 text-sm font-semibold text-slate-900 max-w-[280px] truncate" title={fa.title}>{fa.title}</td>
                  <td className="text-right font-mono-alt text-xs tabular-nums">{fa.amount != null ? formatCurrency(fa.amount) : "—"}</td>
                  <td className="text-xs text-slate-600">{fa.current_stage || fa.status}</td>
                  <td className="text-right font-mono-alt text-xs tabular-nums">{fa.age_hours}h</td>
                  <td className="text-right font-mono-alt text-xs tabular-nums text-rose-600 font-semibold">+{fa.overdue_hours}h</td>
                  <td className="text-xs">{fa.escalated ? <span className="text-amber-700 font-semibold">Notified</span> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────── SoD reference ───────────────
function SodSection() {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/finance/controls/matrix"); setRows(data.segregation_of_duties || []); }
    catch { /* non-blocking */ }
  })(); }, []);
  return (
    <div className="dc-card overflow-hidden" data-testid="sod-section">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <Shield size={18} weight="fill" className="text-amber-600" />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Segregation of Duties</h3>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">Enforced: creator≠approver · approver≠payer · recorder≠reconciler</span>
      </div>
      <div className="responsive-table-wrap">
        <table className="data-table w-full min-w-[860px]" data-testid="controls-sod-table">
          <thead><tr><th>Function</th><th>Responsible</th><th>Must NOT also perform</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.function}>
                <td className="px-5 py-3 font-semibold text-slate-900">{s.function}</td>
                <td className="px-5 py-3 text-sm">{s.responsible}</td>
                <td className="px-5 py-3 text-xs text-red-700">{s.must_not}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────── Main tab ───────────────
export function ControlsTab({ isAdmin, canEdit }) {
  return (
    <div className="space-y-5" data-testid="controls-tab">
      <MatrixSection isAdmin={isAdmin} />
      <PermissionMatrixPanel isAdmin={isAdmin} />
      <DelegationsSection canEdit={canEdit} />
      <EscalationsSection canEdit={canEdit} />
      <SodSection />
    </div>
  );
}
